import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../../common/types';
import { SMLButton } from '../../../../components/primitives/SMLButton';
import { useOpenWatermarkOffer } from '../../../../watermarkUpsell/useOpenWatermarkOffer';
import type { CoverRedo } from './useCoverRedo';
import { CoverRedoPair } from './CoverRedoPair';
import { RedoEnd, RedoWorking } from './RedoRunBanner';
import { kindChips } from './coverRedoKindText';
import { coverRedoMessages as messages } from './coverRedoMessages';
import { withBrandWord } from '../../../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover redo; each part uses its own slice of it */
import styles from './CoverRedo.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CoverRedoBannerProps {
  redo: CoverRedo;
  onRedo: () => void;
  /** The largest `etaSec` of the kit assets that are being made now, or null. */
  etaSec?: number | null;
}

/** The offer (design kit-redo-cover a1, a2, a3). */
const Offer: React.FC<CoverRedoBannerProps> = ({ redo, onRedo }) => {
  const intl = useIntl();
  const openOffer = useOpenWatermarkOffer();
  const removeWatermark = useCallback(() => openOffer('banner'), [openOffer]);
  const { state } = redo;
  const chips = useMemo(() => (state === undefined ? [] : kindChips(intl, state.counts)), [intl, state]);
  if (state === undefined) {
    return null;
  }
  let cost: string;
  if (state.credits === 0) {
    cost = intl.formatMessage(messages.free);
  } else if (state.creditsAvailable === null) {
    cost = intl.formatMessage(messages.costNoLimit, { credits: state.credits });
  } else {
    cost = intl.formatMessage(messages.cost, { credits: state.credits, left: state.creditsAvailable });
  }
  return (
    <section className={styles.banner} data-testid="cover-redo-banner">
      <CoverRedoPair previousUrl={state.cover.previousUrl} currentUrl={state.cover.currentUrl} />
      <div className={styles.body}>
        <h3 className={styles.title}>{intl.formatMessage(messages.bannerTitle, { count: state.total })}</h3>
        <p className={styles.sub}>{intl.formatMessage(messages.bannerBody)}</p>
        <ul className={styles.kinds}>
          {chips.map((chip) => (
            <li key={chip.kind} className={styles.kind}>
              {chip.text}
            </li>
          ))}
        </ul>
        {state.watermark && (
          <span className={styles.watermark}>
            {withBrandWord(intl.formatMessage(messages.watermark))}
            <button type="button" className={styles.link} onClick={removeWatermark}>
              {intl.formatMessage(messages.removeWatermark)}
            </button>
          </span>
        )}
      </div>
      <div className={styles.actions}>
        <div className={styles.buttons}>
          <SMLButton onClick={redo.notNow}>{intl.formatMessage(messages.notNow)}</SMLButton>
          <SMLButton variant="primary" onClick={onRedo}>
            {intl.formatMessage(messages.redo, { count: state.total })}
          </SMLButton>
        </div>
        <span className={clsx(styles.cost, state.credits === 0 && styles.costFree)}>{cost}</span>
      </div>
    </section>
  );
};

/** While the redo runs (c1): ready of total, the bar, and the time left. */
const Working: React.FC<CoverRedoBannerProps> = ({ redo, etaSec }) => {
  const run = redo.state?.run;
  if (run === undefined || run === null) {
    return null;
  }
  return (
    <RedoWorking
      run={run}
      postsCount={redo.state?.scheduledPosts.length ?? 0}
      etaSec={etaSec}
      title={messages.progressTitle}
      testIdPrefix="cover-redo"
    />
  );
};

/** The end of the run (c2, c3): done, or the files that failed with the credits that came back. */
const End: React.FC<CoverRedoBannerProps & { failed: boolean }> = ({ redo, onRedo, failed }) => {
  const run = redo.state?.run;
  if (run === undefined || run === null) {
    return null;
  }
  return (
    <RedoEnd
      run={run}
      failed={failed}
      currentUrl={redo.state?.cover.currentUrl ?? null}
      doneTitle={messages.doneTitle}
      failedBody={messages.failedBody}
      canRetry={redo.state?.outOfDate === true}
      onClose={redo.closeEnd}
      onRetry={onRedo}
      testIdPrefix="cover-redo"
    />
  );
};

/** The one line that stays after "Not now" (a4). */
const Quiet: React.FC<CoverRedoBannerProps> = ({ redo, onRedo }) => {
  const intl = useIntl();
  return (
    <div className={styles.quiet} data-testid="cover-redo-quiet">
      <span>{intl.formatMessage(messages.quietLine, { count: redo.state?.total ?? 0 })}</span>
      <button type="button" className={styles.link} onClick={onRedo}>
        {intl.formatMessage(messages.quietRedo)}
      </button>
    </div>
  );
};

/**
 * The cover redo banner of the Kit and the Posts tab (design kit-redo-cover a, c): the offer, the
 * progress of a run, its end, or the quiet line after "Not now". Nothing while the kit has the cover
 * of the song.
 */
const CoverRedoBannerComponent: React.FC<CoverRedoBannerProps> = ({ redo, onRedo, etaSec }) => {
  switch (redo.banner) {
    case 'offer':
      return <Offer redo={redo} onRedo={onRedo} />;
    case 'working':
      return <Working redo={redo} onRedo={onRedo} etaSec={etaSec} />;
    case 'done':
      return <End redo={redo} onRedo={onRedo} failed={false} />;
    case 'failed':
      return <End redo={redo} onRedo={onRedo} failed />;
    case 'hidden':
      return redo.showsQuietLine ? <Quiet redo={redo} onRedo={onRedo} /> : null;
  }
};

export const CoverRedoBanner = TypedMemo(CoverRedoBannerComponent);
